export const PRICING_PLAN_IDS = ['free', 'basic', 'pro', 'premium'] as const;

export type PricingPlanId = (typeof PRICING_PLAN_IDS)[number];

export interface PricingPlanDefinition {
  id: PricingPlanId;
  price: number;
  originalPrice?: number;
  currency: string;
  tier: number;
  displayOrder: number;
}

export const PRICING_PLANS: readonly PricingPlanDefinition[] = [
  { id: 'free', price: 0, currency: 'EUR', tier: 0, displayOrder: 0 },
  { id: 'basic', price: 495, originalPrice: 795, currency: 'EUR', tier: 1, displayOrder: 1 },
  { id: 'pro', price: 999, currency: 'EUR', tier: 2, displayOrder: 2 },
  { id: 'premium', price: 1999, currency: 'EUR', tier: 3, displayOrder: 3 },
];

export function getPricingPlan(planId: string): PricingPlanDefinition | undefined {
  return PRICING_PLANS.find((plan) => plan.id === planId);
}

export const PRICING_PLAN_ID_BY_TIER: Readonly<Partial<Record<number, PricingPlanId>>> = {
  0: 'free',
  1: 'basic',
  2: 'pro',
  3: 'premium',
};

export function isPricingPlanId(value: string): value is PricingPlanId {
  return (PRICING_PLAN_IDS as readonly string[]).includes(value);
}

export interface PricingPlanCopy {
  name: string;
  description: string;
  features: string[];
}

export interface PricingCopy {
  title: string;
  description: string;
  mostPopular: string;
  perMonth: string;
  perYear: string;
  billing: {
    label: string;
    monthly: string;
    yearly: string;
  };
  priceLabels: {
    original: string;
    earlyAccess: string;
  };
  actions: {
    startWriting: string;
    getStarted: string;
  };
  comparison: {
    title: string;
    description: string;
    action: string;
  };
  plans: Record<PricingPlanId, PricingPlanCopy>;
}

export interface SharedLocaleDictionary {
  pricing: PricingCopy;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Merges shared locale content into an app locale without mutating either input.
 * Shared values win, nested objects are merged recursively, and arrays are replaced.
 */
export function mergeLocales<TBase extends object, TShared extends object>(
  base: TBase,
  shared: TShared,
): TBase & TShared {
  const merged: Record<string, unknown> = {
    ...(base as Record<string, unknown>),
  };

  for (const [key, sharedValue] of Object.entries(shared)) {
    const baseValue = merged[key];

    merged[key] = isPlainObject(baseValue) && isPlainObject(sharedValue)
      ? mergeLocales(baseValue, sharedValue)
      : sharedValue;
  }

  return merged as TBase & TShared;
}
